import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import { ISendXapiStatement } from '@vedalib/courseAnalitycs'
import type { DeepPartial } from '@vedalib/editor/lib/utils/types'
import * as R from 'ramda'

import { DEFAULT_DESKTOP_WIDTH } from 'components/Editor/DeviceEmulator/constants'
import type { CommentMock } from 'components/comments/types'
import { COMMENTS_FILTER_INITIAL_VALUES } from 'components/forms/CommentsFilter/constants'
import { TASKS_FILTER_INITIAL_VALUES } from 'components/forms/TasksEditorFilter/constants'
import type { TaskMock } from 'components/uiKit/Task'
import { SCORM_PATHS } from 'constants/paths'
import type {
  EditorBlockPatchSubscriptionSubscription,
  EmployeePreferencesSchemaFragment,
} from 'gql/__generated__/graphql'
import type { ScrollType } from 'services/Scroll/ScrollService'

import { DEFAULT_SETTINGS } from './constants'
import { DeviceMode, type AppMode, PreviewMode, type SectionTypeEnum } from './enums'
// import { setViewedBlock, setViewedBlocks } from './mutators/analytic'
import { updateBlockState, updateNavigation } from './mutators/common'
import {
  courseContinue,
  courseStart,
  hideNotCompletedSections,
  setCourseStatus,
  setHighlightPreview,
  setIncompleteElements,
  setIncompleteSections,
  setSidebar,
  setSidebarChapter,
  setSidebarSearch,
  showNotCompletedSections,
  hideIncompleteElementList,
  showIncompleteElementList,
} from './mutators/course'
import {
  setAppMode,
  setNavigation,
  setToolbar,
  setSettings,
  setCommentsFilter,
  setDeviceMode,
  setHighlight,
  setProjectNavigation,
  setTasksFilter,
  updateBlock,
  remoteBlockPatch,
  addUndoPatch,
  undoBlock,
  redoBlock,
  resetBlock,
  updateBlockVersion,
  waitBlock,
} from './mutators/editor'
import { updateElement, completeElementInBlock } from './mutators/element'
import { questionReset, questionSetValue, questionValidate, reset } from './mutators/question'
import { testEnd, testRestart, testStart } from './mutators/test'
import type {
  Block,
  ElementState,
  IProjectContext,
  MapType,
  PreviewCourseStatus,
  Project,
  ProjectPaths,
  ScormSettings,
  Section,
  Patch,
  IEditorSettings,
  INavigationState,
  IToolbarState,
  IProjectCommentsFilterValues,
  IProjectTasksFilterValues,
  ProjectUrlType,
  IDndState,
  NodeState,
} from './types'

export interface ICourseNavigationPayload {
  sectionId?: string | null
  blockId?: string | null
  nodeId?: string | null
  scrollBlockId?: string | null
  fromSectionId?: string | null
  fromSectionName?: string | null
  fromSectionType?: SectionTypeEnum | null
}

export interface IIncompleteElement {
  sectionId: string
  blockId: string
  nodeId: string
  elementId: string
}

export interface IShowIncompleteElementListPayload {
  sectionId: string
  incompleteElements: IIncompleteElement[]
}

export interface IIncompleteElement {
  sectionId: string
  blockId: string
  nodeId: string
  elementId: string
}

export interface ISetIncompleteElementListPayload {
  sectionId: string
  incompleteElements: IIncompleteElement[]
}

export interface IHideIncompleteElementListPayload {
  sectionId: string
}

export interface IQuestionPayload {
  elementId: string
  value: ElementState
  isReady: boolean
  blockId: string
  currentTry?: number | null
}

export interface ICompleteElementPayload {
  elementId: string
  blockId: string
  incomplete: boolean
  sectionId?: string
}

export interface IProjectNavigationPayload {
  sectionId?: string
  blockId?: string | null
  nodeIds?: string[] | null
  scroll?: ScrollType | ScrollType[]
  selectedBlocks?: string[]
}

export interface IUpdateBlockPayload {
  id: string
  name?: string
  value?: unknown
}

export interface IResetBlockPayload {
  id: string
  block: Block
}

export interface IWaitBlockPayload {
  id: string
  wait: boolean
}

export interface IUpdateBlockVersionPayload {
  blockId: string
  version: number
}

export interface IUseBlockViewedPayload {
  blockId: string
  viewed: boolean
  hidden?: boolean
}

export type DragStartType = Pick<
  IDndState,
  'type' | 'sourceId' | 'targetIds' | 'initialCoords' | 'box'
>

export type DragMoveType = Pick<
  IDndState,
  'destinationId' | 'currentCoords' | 'zone' | 'areas' | 'coords' | 'index' | 'flex' | 'guides'
>

const initialState: IProjectContext = {
  path: SCORM_PATHS.mainV2,
  urlParams: {
    publicLinkId: '',
    companyId: '',
    projectId: '',
    sectionId: '',
    version: '0',
    courseId: '',
  },
  mode: PreviewMode.scorm,
  data: {
    project: {} as Project,
    sections: {},
    blocks: {},
    comments: [],
    tasks: [],
  },
  state: {
    mode: null,
    comments: {
      filter: COMMENTS_FILTER_INITIAL_VALUES,
      form: { blockId: null, elementId: null },
      opened: null,
      pinned: [],
    },
    tasks: { filter: TASKS_FILTER_INITIAL_VALUES, opened: null },
    sidebar: { open: false, chapter: null },
    deviceMode: DeviceMode.desktop,
    deviceWidth: DEFAULT_DESKTOP_WIDTH,
    editor: {
      syncing: false,
      remotePatches: [],
      undoPatches: [],
      redoPatches: [],
      highlight: null,
      selectedBlocks: [],
      selectedNodes: [],
      settings: DEFAULT_SETTINGS,
      navBar: {
        tab: null,
        showPin: true,
      },
      toolbar: {
        blockSettings: null,
        hidden: true,
        commentTab: null,
        elementTab: null,
        taskTab: null,
        sectionSettings: null,
      },
      metaKey: false,
      shiftKey: false,
      altKey: false,
      dnd: null,
      coordinates: { x: 0, y: 0 },
    },
    course: { status: 'not attempted' },
    sections: {},
    blocks: {},
    elements: {},
    nodes: {},
    scrollReady: {},
    // analytics: { blocksViewed: {} },
  },
}

// TODO: Common question using Draft(mutations, and compose?)  or strong immutable (always return new state with R (types problem with R.assocPath))
export const projectSlice = createSlice({
  name: 'project',
  initialState,
  reducers: {
    // INITIALIZE
    init: (state, action: PayloadAction<DeepPartial<IProjectContext>>) =>
      R.mergeDeepRight(state, action.payload) as IProjectContext,

    reset: () => initialState,

    setPath: (state, action: PayloadAction<ProjectPaths>) => {
      state.path = action.payload
    },
    setDataProject: (state, action: PayloadAction<Project>) => {
      state.data.project = action.payload
    },
    setDataSections: (state, action: PayloadAction<MapType<Section>>) => {
      state.data.sections = action.payload
    },
    setDataBlocks: (state, action: PayloadAction<MapType<Block>>) => {
      state.data.blocks = action.payload
    },
    setDataComments: (state, action: PayloadAction<CommentMock[]>) => {
      state.data.comments = action.payload
    },
    setDataTasks: (state, action: PayloadAction<TaskMock[]>) => {
      state.data.tasks = action.payload
    },
    setScormSettings: (state, action: PayloadAction<ScormSettings | undefined>) => {
      state.data.scormSettings = action.payload
    },
    setUrlParams: (state, action: PayloadAction<ProjectUrlType>) => {
      state.urlParams = R.map((v) => v ?? null, action.payload) as ProjectUrlType
    },

    // COURSE
    courseStart: (state) => courseStart(state),

    courseContinue: (state) => courseContinue(state),

    courseEnd: (state) => setCourseStatus('finished')(state),

    setCourseNavigation: (state, action: PayloadAction<ICourseNavigationPayload>) =>
      updateNavigation(action.payload)(state),

    setCourseStatus: (state, action: PayloadAction<PreviewCourseStatus>) =>
      setCourseStatus(action.payload)(state),

    continueBlock: (state, action: PayloadAction<{ blockId: string }>) =>
      updateBlockState(action.payload.blockId, { continueCompleted: true, viewed: true })(state),

    hideBlock: (state, action: PayloadAction<{ blockId: string }>) =>
      updateBlockState(action.payload.blockId, { hide: true, viewed: true })(state),

    continueAndHideBlock: (state, action: PayloadAction<{ blockId: string }>) =>
      updateBlockState(action.payload.blockId, {
        continueCompleted: true,
        viewed: true,
        hide: true,
      })(state),

    setBlockViewed: (state, action: PayloadAction<string>) =>
      updateBlockState(action.payload, { viewed: true })(state),

    completeElement: (state, action: PayloadAction<ICompleteElementPayload>) =>
      completeElementInBlock(state, action.payload),

    testStart: (state) => testStart(state),

    testRestart: (state) => testRestart(state),

    testEnd: (state) => testEnd(state),

    setQuestion: (state, action: PayloadAction<IQuestionPayload>) =>
      questionSetValue(state, action.payload),

    questionValidate: (state, action: PayloadAction<{ blockId: string }>) =>
      questionValidate(state, action.payload.blockId),

    questionReset: (state, action: PayloadAction<string>) => questionReset(state, action.payload),

    answerReset: (state, action: PayloadAction<string>) => reset(state, action.payload),

    setIncompleteElements: (
      state,
      action: PayloadAction<{ elements: IIncompleteElement[]; sectionId: string }>,
    ) =>
      setIncompleteElements({
        elements: action.payload.elements,
        sectionId: action.payload.sectionId,
      })(state),
    showIncompleteElementList: (state) => showIncompleteElementList(state),

    hideIncompleteElementList: (state) => hideIncompleteElementList(state),

    setIncompleteSections: (state, action: PayloadAction<string[]>) =>
      setIncompleteSections(action.payload)(state),

    showNotCompletedSections: (state) => showNotCompletedSections(state),

    hideNotCompletedSections: (state) => hideNotCompletedSections(state),

    // ANALYTIC
    sendCourseAnalytics: (
      _state,
      _action: PayloadAction<ISendXapiStatement | ISendXapiStatement[]>,
    ) => {},

    // setAnalyticsViewedBlock: (state, action: PayloadAction<IUseBlockViewedPayload>) =>
    //   setViewedBlock(action.payload)(state),

    // setAnalyticsViewedBlocks: (state, action: PayloadAction<IUseBlockViewedPayload[]>) =>
    //   setViewedBlocks(action.payload)(state),

    // PROJECT
    setProjectNavigation: (state, action: PayloadAction<IProjectNavigationPayload>) =>
      setProjectNavigation(action.payload)(state),

    setAppMode: (state, action: PayloadAction<{ mode: AppMode }>) =>
      setAppMode(action.payload.mode)(state),

    setDeviceMode: (state, action: PayloadAction<{ mode: DeviceMode; width?: number | '100%' }>) =>
      setDeviceMode(action.payload.mode, action.payload.width)(state),

    setSidebar: (state, action: PayloadAction<{ open: boolean }>) =>
      setSidebar(action.payload.open)(state),

    setSidebarChapter: (state, action: PayloadAction<{ chapter: Section | null }>) =>
      setSidebarChapter(action.payload.chapter)(state),

    setSidebarSearch: (state, action: PayloadAction<{ search?: string }>) =>
      setSidebarSearch(action.payload.search)(state),

    setToolbar: (state, action: PayloadAction<Partial<IToolbarState>>) =>
      setToolbar(action.payload)(state),

    setNavbar: (state, action: PayloadAction<Partial<INavigationState & { init?: boolean }>>) =>
      setNavigation(action.payload)(state),

    setSettings: (state, action: PayloadAction<Partial<IEditorSettings>>) =>
      setSettings(action.payload)(state),

    setCommentsFilter: (state, action: PayloadAction<IProjectCommentsFilterValues>) =>
      setCommentsFilter(action.payload)(state),

    setTasksFilter: (state, action: PayloadAction<IProjectTasksFilterValues>) =>
      setTasksFilter(action.payload)(state),

    setHighlight: (state, action: PayloadAction<string | null>) =>
      setHighlight(action.payload)(state),

    setHighlightPreview: (state, action: PayloadAction<string | null>) =>
      setHighlightPreview(action.payload)(state),

    updateBlock: (state, action: PayloadAction<IUpdateBlockPayload>) =>
      updateBlock(state, action.payload),

    waitBlock: (state, action: PayloadAction<IWaitBlockPayload>) => {
      waitBlock(state, action.payload)
    },

    updateBlockVersion: (state, action: PayloadAction<IUpdateBlockVersionPayload>) => {
      updateBlockVersion(state, action.payload)
    },

    resetBlock: (state, action: PayloadAction<IResetBlockPayload>) => {
      resetBlock(state, action.payload)
    },

    remoteBlockPatch: (
      state,
      action: PayloadAction<EditorBlockPatchSubscriptionSubscription['data']>,
    ) => {
      remoteBlockPatch(state, action.payload)
    },

    addUndoPatch: (state, action: PayloadAction<Patch>) => {
      addUndoPatch(state, action.payload)
    },

    undoBlock: (state) => {
      undoBlock(state)
    },

    redoBlock: (state) => {
      redoBlock(state)
    },

    setSyncing: (state, action: PayloadAction<boolean | 'error'>) => {
      state.state.editor.syncing = action.payload
    },

    // COMMON
    setOpenedTask: (
      state,
      action: PayloadAction<{
        sectionId?: string | null
        blockId?: string | null
        taskId?: string | null
      } | null>,
    ) => {
      if (action.payload?.sectionId) {
        state.urlParams.sectionId = action.payload.sectionId
      }

      state.state.tasks.opened = action.payload
    },
    setOpenedComment: (
      state,
      action: PayloadAction<{
        sectionId?: string | null
        blockId?: string | null
        nodeId?: string | null
        threadId?: string | null
      } | null>,
    ) => {
      state.state.comments.opened = action.payload
      state.state.comments.form = { blockId: null, elementId: null }

      const { sectionId, blockId } = action.payload || {}
      if (sectionId) {
        Object.assign(state, setProjectNavigation({ sectionId, blockId, nodeIds: null })(state))
      }
    },
    setElementNavigation: (
      state,
      action: PayloadAction<{
        sectionId?: string | null
        blockId?: string | null
        nodeId?: string | null
        elementId?: string | null
      } | null>,
    ) => {
      const { sectionId, blockId, nodeId } = action.payload || {}
      if (sectionId) {
        Object.assign(
          state,
          setProjectNavigation({
            sectionId,
            blockId,
            nodeIds: nodeId ? [nodeId] : null,
          })(state),
        )
      }
    },
    setCommentForm: (
      state,
      {
        payload,
      }: PayloadAction<{
        open?: true
        blockId: string | null
        elementId: string | null
      } | null>,
    ) => {
      if (payload && (payload.open || !state.state.comments.form.blockId)) {
        state.state.comments.form = payload
      } else {
        state.state.comments.form = { blockId: null, elementId: null }
      }
    },
    togglePinnedComment: (state, { payload }: PayloadAction<{ id: string }>) => {
      const index = state.state.comments.pinned.indexOf(payload.id)
      if (index !== -1) {
        state.state.comments.pinned.splice(index, 1)
      } else {
        state.state.comments.pinned.unshift(payload.id)
      }

      state.state.editor.navBar.showPin = true
    },
    setScrollWait: (state, action: PayloadAction<{ id: string; ready: boolean }>) => {
      state.state.scrollReady[action.payload.id] = action.payload.ready
    },
    clearScrollWait: (state) => {
      state.state.scrollReady = {}
    },
    setElement: (state, action: PayloadAction<{ id: string; value: ElementState }>) =>
      updateElement(action.payload.id, action.payload.value)(state),
    scroll: (_state, _action: PayloadAction<ScrollType | ScrollType[]>) => {},
    setEmployeeToProjectPreferences: (
      state,
      action: PayloadAction<EmployeePreferencesSchemaFragment>,
    ) => {
      state.data.project.employeePreferences = R.mergeDeepRight(
        state.data.project.employeePreferences || {},
        action.payload,
      )
    },

    sendReflection: (
      state,
      action: PayloadAction<{
        value: string
        elementId: string
        questionText: string
      }>,
    ) => {
      const { elementId, value } = action.payload
      state.state.elements[elementId] = updateElement(elementId, { value })(state).state.elements[
        elementId
      ]
    },
    // PRO_MODE_2
    // NODE_STATE
    updateNodeStates: (
      state,
      action: PayloadAction<{ nodeIds: string[]; state: Partial<NodeState> }>,
    ) => {
      const { nodeIds, state: newState } = action.payload
      nodeIds.forEach((nodeId) => {
        state.state.nodes[nodeId] = { ...state.state.nodes[nodeId], ...newState }
      })
    },
    updateNodeState: (state, action: PayloadAction<{ id: string; state: Partial<NodeState> }>) => {
      const { id, state: newState } = action.payload
      state.state.nodes[id] = { ...state.state.nodes[id], ...newState }
    },
    setNodeTransformState: (
      state,
      action: PayloadAction<{ nodeId: string; transform: Partial<NodeState['transform']> }>,
    ) => {
      const { nodeId, transform } = action.payload
      state.state.nodes[nodeId] = {
        ...state.state.nodes[nodeId],
        transform:
          transform === null ? null : { ...state.state.nodes[nodeId]?.transform, ...transform },
      }
    },
    setOpenNode: (state, action: PayloadAction<{ nodeId: string; open: boolean }>) => {
      const { nodeId, open } = action.payload
      state.state.nodes[nodeId] = { ...state.state.nodes[nodeId], open }
    },
    setShowGrid: (state, action: PayloadAction<{ nodeId: string; showGrid: boolean }>) => {
      const { nodeId, showGrid } = action.payload
      state.state.nodes[nodeId] = { ...state.state.nodes[nodeId], showGrid }
    },
    playAnimation: (state, action: PayloadAction<{ nodeId: string; playing: boolean }>) => {
      state.state.nodes[action.payload.nodeId] = {
        ...state.state.nodes[action.payload.nodeId],
        playing: action.payload.playing,
      }
    },

    // DND
    dragStart: (state, action: PayloadAction<DragStartType>) => {
      if (!state.state.editor.dnd) {
        state.state.editor.dnd = {
          zone: null,
          currentCoords: null,
          areas: null,
          coords: null,
          flex: null,
          index: null,
          guides: null,
          destinationId: action.payload.sourceId,
          ...action.payload,
        }
      }
    },
    dragMove: (state, action: PayloadAction<DragMoveType>) => {
      if (state.state.editor.dnd) {
        state.state.editor.dnd = { ...state.state.editor.dnd, ...action.payload }
      }
    },
    dragEnd: (state) => {
      state.state.editor.dnd = null
    },
    setAnchor: (state, action: PayloadAction<{ x?: number; y?: number }>) => {
      const { x, y } = action.payload
      if (state.state.editor.dnd) {
        state.state.editor.dnd.anchorX = x ?? state.state.editor.dnd.anchorX
        state.state.editor.dnd.anchorY = y ?? state.state.editor.dnd.anchorY
      }
    },

    // KEYBOARD_KEYS
    setMetaKey: (state, action: PayloadAction<boolean>) => {
      state.state.editor.metaKey = action.payload
    },
    setShiftKey: (state, action: PayloadAction<boolean>) => {
      state.state.editor.shiftKey = action.payload
    },
    setAltKey: (state, action: PayloadAction<boolean>) => {
      state.state.editor.altKey = action.payload
    },
    setMouseCoordinates: (state, action: PayloadAction<{ x: number; y: number }>) => {
      state.state.editor.coordinates.x = action.payload.x
      state.state.editor.coordinates.y = action.payload.y
    },
  },
})

export const actions = projectSlice.actions

export type ProjectActions = {
  [key in keyof typeof actions]: (typeof actions)[key]
}

export type ProjectActionsTypes = {
  [key in keyof typeof actions]: (typeof actions)[key] extends { type: infer T } ? T : never
}

export default projectSlice.reducer
